import React, { useEffect, useRef, useState } from "react";
import "./Speakers2.css";

export default function Speakers2() {
  const [speakers, setSpeakers] = useState([]);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [error, setError] = useState("");

  const closeButtonRef = useRef(null);
  const triggerRef = useRef(null);
  const modalRef = useRef(null);

  // ================= LOAD SPEAKERS =================

  useEffect(() => {
    const controller = new AbortController();

    fetch("/data/speakers2.json", {
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error: ${res.status}`);
        }

        return res.json();
      })
      .then((data) => {
        // Exclude withdrawn speakers
        setSpeakers(data.filter((speaker) => !speaker.withdrawn));
      })
      .catch((err) => {
        if (err.name === "AbortError") return;

        console.error("Failed to load speakers2.json:", err);
        setError("Unable to load speaker information.");
      });

    return () => controller.abort();
  }, []);

  // ================= OPEN POPUP =================

  const openSpeaker = (speaker, event) => {
    triggerRef.current = event.currentTarget;
    setSelectedSpeaker(speaker);
  };

  // ================= CLOSE POPUP =================

  const closeSpeaker = () => {
    setSelectedSpeaker(null);
  };

  // ================= MODAL BEHAVIOUR =================

  useEffect(() => {
    if (!selectedSpeaker) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeSpeaker();
        return;
      }

      if (event.key !== "Tab") return;

      const modal = modalRef.current;
      if (!modal) return;

      const focusable = Array.from(
        modal.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );

      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);

      requestAnimationFrame(() => {
        triggerRef.current?.focus();
      });
    };
  }, [selectedSpeaker]);

  // ================= SOCIAL LINKS =================

  const renderSocialLinks = (speaker) => {
    const links = [
      {
        url: speaker.email ? `mailto:${speaker.email}` : null,
        icon: "fas fa-envelope",
        label: "Email",
      },
      {
        url: speaker.facebook,
        icon: "fab fa-facebook",
        label: "Facebook",
      },
      {
        url: speaker.instagram,
        icon: "fab fa-instagram",
        label: "Instagram",
      },
      {
        url: speaker.linkedin,
        icon: "fab fa-linkedin",
        label: "LinkedIn",
      },
      {
        url: speaker.youtube,
        icon: "fab fa-youtube",
        label: "YouTube",
      },
      {
        url: speaker.website,
        icon: "fas fa-globe",
        label: "Website",
      },
    ];

    const availableLinks = links.filter((link) => link.url);

    if (availableLinks.length === 0) {
      return null;
    }

    return (
      <div className="speaker-social-links">
        {availableLinks.map((link) => (
          <a
            key={link.label}
            href={link.url}
            title={link.label}
            aria-label={`${speaker.name} - ${link.label}`}
            target={
              link.url.startsWith("mailto:")
                ? undefined
                : "_blank"
            }
            rel={
              link.url.startsWith("mailto:")
                ? undefined
                : "noopener noreferrer"
            }
          >
            <i className={`${link.icon} fa-lg`} aria-hidden="true"></i>
          </a>
        ))}
      </div>
    );
  };

  // ================= RENDER =================

  return (
    <section id="speakers2" className="text-white py-5">
      <div className="container">

        {/* ================= HEADING ================= */}

        <h2 className="text-center mb-2">
          TEDxSetapak 2026 Speakers
        </h2>

        <p className="speaker-theme">
          TUMBUH - Growth Begins With People
        </p>

        <p className="text-center text-white-50 mb-5">
          Click on a speaker to discover their story and talk.
        </p>

        {/* ================= ERROR ================= */}

        {error && (
          <p className="text-center text-danger">
            {error}
          </p>
        )}

        {/* ================= SPEAKER GRID ================= */}

        <div className="row justify-content-center g-3">
          {speakers.map((speaker, index) => (
            <div
              className="col-12 col-md-4 col-lg-3 d-flex"
              key={speaker.id ?? index}
            >
              <button
                type="button"
                className="speaker-gallery-card w-100"
                onClick={(event) => openSpeaker(speaker, event)}
                aria-label={`View details for ${speaker.name}`}
              >

                {/* Speaker Image */}

                <div className="speaker-gallery-image">
                  <img
                    src={`/image/${speaker.image}`}
                    alt={speaker.name}
                    loading="lazy"
                  />
                </div>

                {/* Speaker Information */}

                <div className="speaker-gallery-info">
                  <h4 className="speaker-gallery-name">
                    {speaker.name}
                  </h4>

                  {(speaker.role1 || speaker.role2 || speaker.role3) && (
                    <p className="speaker-gallery-role">
                      {[speaker.role1, speaker.role2, speaker.role3]
                        .filter(Boolean)
                        .map((role, index) => (
                          <React.Fragment key={index}>
                            {index > 0 && <br />}
                            {role}
                          </React.Fragment>
                        ))}
                    </p>
                  )}

                  <div className="speaker-gallery-action">
                    View Speaker Details
                    <i
                      className="fas fa-arrow-right ms-2"
                      aria-hidden="true"
                    ></i>
                  </div>
                </div>

              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ================= SPEAKER POPUP ================= */}

      {selectedSpeaker && (
        <div
          className="speaker-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeSpeaker();
            }
          }}
        >
          <div
            ref={modalRef}
            className="speaker-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="speaker-modal-title"
          >

            {/* ================= MODAL HEADER ================= */}

            <div className="speaker-modal-header">
              <h4 id="speaker-modal-title" className="mb-0">
                {selectedSpeaker.name}
              </h4>

              <button
                ref={closeButtonRef}
                type="button"
                className="speaker-modal-close"
                onClick={closeSpeaker}
                aria-label="Close speaker details"
              >
                &times;
              </button>
            </div>

            {/* ================= MODAL BODY ================= */}

            <div className="speaker-modal-body">

              {/* ================= SPEAKER PROFILE ================= */}

              <div className="speaker-modal-profile">

                {/* Speaker Photo */}

                <img
                  src={`/image/${selectedSpeaker.image}`}
                  alt={selectedSpeaker.name}
                  className="speaker-modal-photo"
                />

                {/* Speaker Information */}

                <div className="speaker-modal-profile-info">

                  {/* Name */}

                  <h3 className="speaker-modal-name">
                    {selectedSpeaker.name}
                  </h3>

                  {/* Role */}

                  {(selectedSpeaker.role1 ||
                    selectedSpeaker.role2 ||
                    selectedSpeaker.role3) && (
                    <p className="speaker-modal-role">
                      {[
                        selectedSpeaker.role1,
                        selectedSpeaker.role2,
                        selectedSpeaker.role3
                      ]
                        .filter(Boolean)
                        .map((role, index) => (
                          <React.Fragment key={index}>
                            {index > 0 && <br />}
                            {role}
                          </React.Fragment>
                        ))}
                    </p>
                  )}

                  {/* Social Media - Below Name / Role / Category */}

                  {renderSocialLinks(selectedSpeaker)}

                </div>
              </div>

              {/* ================= TOPIC ================= */}

              {selectedSpeaker.title && (
                <div className="speaker-modal-section">
                  <h5>Topic</h5>

                  <p className="speaker-modal-topic">
                    {selectedSpeaker.title}
                  </p>
                </div>
              )}

              {/* ================= ABOUT THE TALK ================= */}

              {selectedSpeaker.description && (
                <div className="speaker-modal-section">
                  <h5>About the Talk</h5>

                  <p>{selectedSpeaker.description}</p>
                </div>
              )}

              {/* ================= EDUCATION ================= */}

              {selectedSpeaker.education && (
                <div className="speaker-modal-section">
                  <h5>Education & Expertise</h5>

                  <p className="fst-italic">
                    {selectedSpeaker.education}
                  </p>
                </div>
              )}

            </div>

            {/* ================= MODAL FOOTER ================= */}

            <div className="speaker-modal-footer">
              <button
                type="button"
                className="btn btn-outline-light"
                onClick={closeSpeaker}
              >
                Close
              </button>
            </div>

          </div>
        </div>
      )}
    </section>
  );
}